import {EventEmitter, Injectable} from '@angular/core';
import {isKIOSKPOLIS, isMobile, isNestBank} from '../../../../utils/utils';
import {ApplicationStateService} from '../../../../../shared/services/application-state.service';
import {DeviceService} from '../../../../../shared/services/device.service';
import {ApplicationContextService} from '../../configuration/application-context.service';
import {AztecScannerService} from '../../../../../shared/services/aztec-scanner.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {CalculationIntegrationParameterName} from '../../../../models/calculation-data.model';
import {CalculationService} from '../../calculation.service';
import {DialogKey} from '../../../../../shared/components/dialogs/dialogs-configuration';
import {
  ImagePreviewDialogComponent
} from '../../../../../shared/components/dialogs/image-preview-dialog/image-preview-dialog.component';
import {DialogService} from '../../../dialog.service';
import {Validator} from '../../../../../shared/models/validator.enum';
import {VehicleDataModel} from '../../../../models/vehicle-data.model';
import {CalculationStorageService} from '../../../../../shared/services/calculation-storage.service';
import {VehicleModelStateService} from '../vehicle-model/vehicle-model-state.service';
import {OwnerDataModel} from '../../../../models/owner-data/owner-data.model';
import {InsurerData} from '../../../../models/insurer-data.model';
import {DateService} from '../../../../../shared/services/date.service';
import {RegExpKey, RegExpService} from '../../../../../shared/services/reg-exp.service';

@Injectable({
  providedIn: 'root'
})
export class InitialDataStateService {

  birthDateChange$: EventEmitter<void> = new EventEmitter<void>();
  customerAgreementScanningMode: boolean = false;

  registrationNumberValidators: any = [
    Validator.REQUIRED_NOT_BLANK,
    {
      name: Validator.MAX_LENGTH,
      maxLength: 8
    },
    {
      name: Validator.PATTERN,
      pattern: this.regExpService.getRegExp(RegExpKey.REGISTRATION_NUMBER)
    },
    {
      name: Validator.MIN_LENGTH,
      minLength: 4
    }
  ];

  private readonly UNKNOWN_REGISTRATION_NUMBER_VALUE = 'BRAK';

  get aztecScannedFromQrCode(): boolean {
    return this.stateService.state.aztecScannedFromQrCode;
  }

  get isDiagnosticStationContext(): boolean {
    return this.contextService.isDiagnosticStationContext;
  }

  get isMobile() {
    return isMobile();
  }

  get aztecScannerActive(): boolean {
    return this.stateService.aztecScannerActive;
  }

  get sectionFormVisible(): boolean {
    if (this.isDiagnosticStationContext) {
      return this.aztecScannedFromQrCode;
    }
    return true;
  }

  get sectionVisible(): boolean {
    return !this.aztecScannerActive && this.sectionFormVisible && !this.customerAgreementScanningMode;
  }

  get aztecScannedCompletedActionInfoVisible(): boolean {
    if (isKIOSKPOLIS() && this.isDiagnosticStationContext) {
      return isKIOSKPOLIS() && !this.aztecScannerActive && this.aztecScannedFromQrCode && !this.customerAgreementScanningMode;
    }
    return false;
  }

  get aztecScannerInfoVisible(): boolean {
    if (this.isDiagnosticStationContext) {
      return this.deviceService.isMobileDevice && isKIOSKPOLIS() && !this.aztecScannerActive && !this.aztecScannedFromQrCode;
    }
    return !this.isMobile && (isKIOSKPOLIS() || isNestBank()) && !this.aztecScannerActive;
  }

  get diagnosticStationProcessInfoVisible(): boolean {
    return this.isDiagnosticStationContext ? !this.deviceService.isMobileDevice && isKIOSKPOLIS() : false;
  }

  get customerAgreementPhoto(): string {
    return this.calculationService.getIntegrationParameterValue(CalculationIntegrationParameterName.CUSTOMER_AGREEMENT_PHOTO);
  }

  get customerAgreementPhotoCompletedActionInfoVisible(): boolean {
    return (isKIOSKPOLIS() || isNestBank()) && !this.customerAgreementScanningMode && !this.aztecScannerActive && isNotNullOrUndefined(this.customerAgreementPhoto);
  }

  get scanClientAgreementButtonVisible(): boolean {
    return (isKIOSKPOLIS() || isNestBank()) && this.isDiagnosticStationContext && !this.aztecScannerActive && this.aztecScannedFromQrCode &&
        !this.customerAgreementScanningMode && !this.customerAgreementPhotoCompletedActionInfoVisible;
  }

  get diagnosticStationScanView(): boolean {
    return !this.aztecScannedFromQrCode && this.isDiagnosticStationContext;
  }

  get footerVisible(): boolean {
    return (isKIOSKPOLIS() || isNestBank()) && !this.aztecScannedFromQrCode;
  }

  get unknownRegistrationNumber(): boolean {
    return this.stateService.state.unknownRegistrationNumber;
  }

  get vehicleData(): VehicleDataModel {
    return this.calculationService.vehicleData;
  }

  get ownerData(): OwnerDataModel {
    return this.calculationService.ownerData;
  }

  get insurerData(): InsurerData {
    return this.calculationService.insurerData;
  }

  get unknownRegistrationNumberValue(): string {
    return this.UNKNOWN_REGISTRATION_NUMBER_VALUE;
  }

  get isRegistrationNumberUnknown(): boolean {
    return this.vehicleData.registrationNumber === this.unknownRegistrationNumberValue;
  }

  constructor(private stateService: ApplicationStateService,
              private contextService: ApplicationContextService,
              private deviceService: DeviceService,
              private calculationService: CalculationService,
              private calculationStorageService: CalculationStorageService,
              private aztecScannerService: AztecScannerService,
              private vehicleModelStateService: VehicleModelStateService,
              private dialogService: DialogService,
              private regExpService: RegExpService) { }

  scan(): void {
    this.aztecScannerService.scan();
  }

  openAztecScanner(): void {
    this.aztecScannerService.openAztecScanner();
  }

  closeAztecScanner(scanningSuccess: boolean): void {
    this.aztecScannerService.closeAztecScanner(scanningSuccess);
  }

  onCustomerAgreementCaptureResult(data: string | null): void {
    this.customerAgreementScanningMode = false;
    if (isNotNullOrUndefined(data)) {
      this.calculationService.setCustomerAgreementPhoto(data);
    }
  }

  openCustomerAgreementPhotoPreview(): void {
    this.dialogService.open(DialogKey.IMAGE_PREVIEW_DIALOG, ImagePreviewDialogComponent.getConfigBuilder().buildConfiguration({
      imageData: this.customerAgreementPhoto
    }));
  }

  changeRegistrationNumber(registrationNumber: string): void {
    if (this.unknownRegistrationNumber) {
      this.vehicleData.registrationNumber = this.UNKNOWN_REGISTRATION_NUMBER_VALUE;
    } else {
      this.vehicleData.registrationNumber = (registrationNumber && registrationNumber.length) ? registrationNumber : null;
    }
    this.calculationStorageService.updateCalculation();
  }

  changeBirthDate(birthDate: Date): void {
    birthDate = DateService.shiftToLocalAsUTC(new Date(birthDate));
    this.ownerData.birthDate = birthDate;
    this.insurerData.birthDate = birthDate;
    this.calculationStorageService.updateCalculation();
    this.birthDateChange$.emit();
  }

  registrationNumberChangeEvent(event: any) {
    this.vehicleModelStateService.handleRegistrationNumberChange(event.value);
  }

  changeUnknownRegistrationNumber(value: boolean): void {
    this.stateService.state.unknownRegistrationNumber = value;
  }

  handleUnknownRegistrationNumberChange(value: boolean): void {
    this.changeUnknownRegistrationNumber(value);
    if (value) {
      this.vehicleModelStateService.registrationNumberChange(this.vehicleModelStateService.unknownRegistrationNumberValue);
    }
    this.stateService.updateApplicationState();
  }
}
